"use client";

import { NumberInput } from "@/components/ui/number-input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useI18n } from "@/lib/i18n/hook";
import { useSimulation } from "@/components/simulation-context";
import { SIMULATION_CONFIG, PERIOD_OPTIONS } from "@/lib/constants/investment";
import {
  toLogScale,
  fromLogScale,
  roundToNiceNumber,
} from "@/lib/utils/investment";

export function InvestmentForm() {
  const {
    investmentAmount,
    setInvestmentAmount,
    initialInvestment,
    setInitialInvestment,
    investmentPeriod,
    setInvestmentPeriod,
    returnRate,
    setReturnRate,
    volatility,
    setVolatility,
    targetAmount,
    setTargetAmount,
  } = useSimulation();

  const { t } = useI18n();

  return (
    <div className="space-y-6 p-1">
      <div className="space-y-2">
        <div className="flex justify-between">
          <Label htmlFor="initial-investment">
            {t("riskSimulation.initialInvestment")}
          </Label>
          <NumberInput
            value={initialInvestment}
            onValueChange={(val) => {
              const value = val ?? 0;
              if (
                value >= 0 &&
                value <= SIMULATION_CONFIG.MAX_INITIAL_INVESTMENT
              ) {
                setInitialInvestment(value);
              }
            }}
            className="w-32 text-right"
          />
        </div>
        <Slider
          id="initial-investment"
          min={0}
          max={100}
          step={1}
          value={[
            toLogScale(
              initialInvestment,
              1000,
              SIMULATION_CONFIG.MAX_INITIAL_INVESTMENT
            ),
          ]}
          onValueChange={(value) => {
            const rawValue = fromLogScale(
              value[0],
              1000,
              SIMULATION_CONFIG.MAX_INITIAL_INVESTMENT
            );
            setInitialInvestment(roundToNiceNumber(rawValue));
          }}
          className="mt-2"
        />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>0</span>
          <span>
            {SIMULATION_CONFIG.MAX_INITIAL_INVESTMENT.toLocaleString()}
          </span>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between">
          <Label htmlFor="investment-amount">
            {t("riskSimulation.monthlyInvestment")}
          </Label>
          <NumberInput
            value={investmentAmount}
            onValueChange={(val) => {
              const value = val ?? 0;
              if (
                value >= 0 &&
                value <= SIMULATION_CONFIG.MAX_MONTHLY_INVESTMENT
              ) {
                setInvestmentAmount(value);
              }
            }}
            className="w-32 text-right"
          />
        </div>
        <Slider
          id="investment-amount"
          min={0}
          max={100}
          step={1}
          value={[
            toLogScale(
              investmentAmount,
              1000,
              SIMULATION_CONFIG.MAX_MONTHLY_INVESTMENT
            ),
          ]}
          onValueChange={(value) => {
            const rawValue = fromLogScale(
              value[0],
              1000,
              SIMULATION_CONFIG.MAX_MONTHLY_INVESTMENT
            );
            setInvestmentAmount(roundToNiceNumber(rawValue));
          }}
          className="mt-2"
        />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>0</span>
          <span>
            {SIMULATION_CONFIG.MAX_MONTHLY_INVESTMENT.toLocaleString()}
          </span>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="investment-period">
          {t("riskSimulation.investmentPeriod")}
        </Label>
        <Select
          value={investmentPeriod.toString()}
          onValueChange={(value) => setInvestmentPeriod(Number.parseInt(value))}
        >
          <SelectTrigger id="investment-period">
            <SelectValue placeholder={t("riskSimulation.investmentPeriod")} />
          </SelectTrigger>
          <SelectContent>
            {PERIOD_OPTIONS.map((period) => (
              <SelectItem key={period} value={period.toString()}>
                {period}
                {t("common.years")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="return-rate">
          {t("riskSimulation.annualReturnRate")}
        </Label>
        <NumberInput
          id="return-rate"
          min={0}
          max={100}
          step={0.1}
          value={returnRate}
          onValueChange={(val) => setReturnRate(val ?? 0)}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="volatility">{t("riskSimulation.volatility")}</Label>
        <NumberInput
          id="volatility"
          min={0}
          max={100}
          step={0.1}
          value={volatility}
          onValueChange={(val) => setVolatility(val ?? 0)}
        />
      </div>

      <div className="space-y-2">
        <div className="flex justify-between">
          <Label htmlFor="target-amount">
            {t("riskSimulation.targetAmount")}
          </Label>
          <NumberInput
            value={targetAmount}
            onValueChange={(val) => {
              const value = val ?? 0;
              if (
                value >= 0 &&
                value <= SIMULATION_CONFIG.MAX_INITIAL_INVESTMENT
              ) {
                setTargetAmount(value);
              }
            }}
            className="w-32 text-right"
          />
        </div>
        <Slider
          id="target-amount"
          min={0}
          max={100}
          step={1}
          value={[
            toLogScale(
              targetAmount,
              1000,
              SIMULATION_CONFIG.MAX_INITIAL_INVESTMENT
            ),
          ]}
          onValueChange={(value) => {
            const rawValue = fromLogScale(
              value[0],
              1000,
              SIMULATION_CONFIG.MAX_INITIAL_INVESTMENT
            );
            setTargetAmount(roundToNiceNumber(rawValue));
          }}
          className="mt-2"
        />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>0</span>
          <span>
            {SIMULATION_CONFIG.MAX_INITIAL_INVESTMENT.toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );
}
